/**
 * Asking a customer to pay.
 *
 * A *payment request* is the office saying "here is what you owe, here is where
 * to pay it". A *payment* is money that actually arrived. Keeping them separate
 * is the whole design: a request can be sent, resent, expire, be cancelled, or
 * be paid by some other means entirely, and none of that should touch the
 * ledger. Only a settled charge writes a Payment row.
 *
 * This module owns every rule about that lifecycle, and it is deliberately free
 * of any processor's vocabulary — no "PaymentIntent", no "checkout session".
 * Stripe, Square and Clover all reduce to the same five states, and the adapter
 * is what translates.
 *
 * Nothing here talks to a network, which is why it is the part of the payment
 * work that can be tested properly.
 */
import { assertCents, minCents } from './money.js';
import { maxApplicablePayment } from './totals.js';
// --- states -----------------------------------------------------------------
export const PAYMENT_REQUEST_STATUSES = [
    'pending',
    'processing',
    'paid',
    'failed',
    'cancelled',
    'expired',
];
export const PAYMENT_REQUEST_STATUS_LABELS = {
    pending: 'Awaiting payment',
    processing: 'Payment in progress',
    paid: 'Paid',
    failed: 'Payment failed',
    cancelled: 'Cancelled',
    expired: 'Expired',
};
/** States that can still change on their own. Everything else is history. */
export const OPEN_PAYMENT_REQUEST_STATUSES = [
    'pending',
    'processing',
];
export function isOpenPaymentRequest(status) {
    return OPEN_PAYMENT_REQUEST_STATUSES.includes(status);
}
/**
 * How the customer was asked.
 *
 * `link` covers text, email and a QR code held up at the door — they are all
 * the same hosted page, and the difference is only how the URL travelled.
 */
export const PAYMENT_REQUEST_CHANNELS = ['link', 'terminal', 'virtual_terminal'];
export const PAYMENT_REQUEST_CHANNEL_LABELS = {
    link: 'Payment link',
    terminal: 'Card reader',
    virtual_terminal: 'Card entered by staff',
};
// --- the state machine ------------------------------------------------------
/**
 * `paid` is terminal and one-way.
 *
 * The reason is worth stating: processors retry webhooks, deliver them out of
 * order, and occasionally deliver a `failed` for an earlier attempt *after* the
 * successful one. A machine that let `paid` move backwards would mark a
 * settled invoice unpaid because of a stale message about a card the customer
 * had already replaced.
 */
const ALLOWED = {
    pending: ['processing', 'paid', 'failed', 'cancelled', 'expired'],
    // A failed attempt leaves the request open — the customer can try again on
    // the same link with a different card.
    processing: ['paid', 'failed', 'cancelled', 'expired'],
    failed: ['processing', 'paid', 'cancelled', 'expired'],
    paid: [],
    cancelled: [],
    expired: [],
};
export function canTransitionPaymentRequest(from, to) {
    if (from === to)
        return true;
    return ALLOWED[from].includes(to);
}
/**
 * Fold one reported status into the current one.
 *
 * Returns the status to store, never throws, and never moves backwards out of a
 * terminal state — an out-of-order webhook is an ordinary event here, not an
 * error, so it is reported as `ok: false` with the existing status preserved.
 */
export function applyPaymentRequestStatus(current, reported) {
    if (current === reported)
        return { ok: true, status: current };
    if (canTransitionPaymentRequest(current, reported)) {
        return { ok: true, status: reported };
    }
    return {
        ok: false,
        status: current,
        reason: `A ${current} payment request cannot become ${reported}`,
    };
}
/**
 * Decide what a new request may ask for.
 *
 * Two invariants:
 *
 * 1. Never ask for more than the invoice still owes. Overpayment is a thing
 *    that happens at a counter with cash, not something to build a link for.
 * 2. Never ask for more than the balance *minus what other open links could
 *    already collect*. Sending a second link for the full amount while the
 *    first is still live is how a customer pays twice, and a refund is a far
 *    worse experience than a declined second link.
 */
export function resolvePaymentRequestAmount(input) {
    const balance = maxApplicablePayment(input.totals);
    const alreadyRequested = input.openRequestCents ?? 0;
    const available = balance - alreadyRequested;
    if (balance <= 0) {
        return { ok: false, amountCents: 0, reason: 'This invoice has nothing left to pay' };
    }
    if (available <= 0) {
        return {
            ok: false,
            amountCents: 0,
            reason: 'The full balance is already covered by an open payment request',
        };
    }
    if (input.requestedCents === null || input.requestedCents === undefined) {
        return { ok: true, amountCents: available };
    }
    let requested;
    try {
        requested = assertCents(input.requestedCents, 'requested amount');
    }
    catch {
        return { ok: false, amountCents: 0, reason: 'Amount must be a whole number of cents' };
    }
    if (requested <= 0) {
        return { ok: false, amountCents: 0, reason: 'Amount must be greater than zero' };
    }
    if (requested > available) {
        return {
            ok: false,
            amountCents: 0,
            reason: alreadyRequested > 0
                ? 'That is more than the balance not already covered by an open request'
                : 'That is more than this invoice owes',
        };
    }
    return { ok: true, amountCents: minCents(requested, available) };
}
// --- expiry -----------------------------------------------------------------
/** Long enough for a customer to get home and find their wallet. */
export const DEFAULT_PAYMENT_LINK_TTL_HOURS = 72;
export function paymentRequestExpiry(createdAt, ttlHours = DEFAULT_PAYMENT_LINK_TTL_HOURS) {
    return new Date(createdAt.getTime() + ttlHours * 3_600_000);
}
/**
 * Has this request timed out?
 *
 * `processing` is deliberately excluded: a charge the processor has already
 * accepted must not be expired out from under it just because the clock passed
 * a threshold while the bank was thinking.
 */
export function isPaymentRequestExpired(request, now = new Date()) {
    if (request.status !== 'pending')
        return false;
    if (!request.expiresAt)
        return false;
    const expires = request.expiresAt instanceof Date ? request.expiresAt : new Date(request.expiresAt);
    return expires.getTime() <= now.getTime();
}
/**
 * The status to show a human, accounting for a lapsed clock.
 *
 * Expiry is a fact about time, not an event anybody publishes, so a request can
 * be expired in reality while still reading `pending` in the database. Rather
 * than run a sweeper job, every read passes through here.
 */
export function effectivePaymentRequestStatus(request, now = new Date()) {
    return isPaymentRequestExpired(request, now) ? 'expired' : request.status;
}
/**
 * The line that goes in the text message.
 *
 * Kept here rather than in the SMS code because it is the same sentence whether
 * it is sent by the office, by a tech at the door, or by an automated reminder,
 * and three copies of it would drift.
 */
export function paymentRequestMessage(summary, url, formatAmount) {
    return (`${summary.companyName}: invoice #${summary.invoiceNumber} for ` +
        `${formatAmount(summary.amountCents, summary.currency)}. ` +
        `You can pay securely here: ${url}`);
}
/**
 * Money that has genuinely arrived is always recorded, even when it no longer
 * matches the invoice.
 *
 * The temptation is to reject a settlement that exceeds the balance. That would
 * be wrong: the customer's card has already been charged, and refusing to
 * record it does not give them their money back — it just hides it. Record it,
 * mark it, and let a person deal with the refund.
 */
export function reconcileSettlement(input) {
    const applyCents = assertCents(input.settledCents, 'settled amount');
    return {
        applyCents,
        short: applyCents < input.requestedCents,
        overpaid: applyCents > input.balanceCents,
    };
}
//# sourceMappingURL=payment-requests.js.map